import { OrganizationDetailFragment, useEmployeeQuery } from '@alice-financial/alice-sdk'
import {
  Chip,
  Divider,
  Drawer,
  Grid,
  List,
  ListItem,
  ListItemLink,
  ListItemText,
  Toolbar,
  Typography,
} from '@alice-financial/pretext-ui'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import * as React from 'react'
import { useLocation, useParams } from 'react-router'
import { useMayManageAliceCardProgram } from './aliceCardProgram/aliceCardProgramUtils'
import { useKYCStatus } from './aliceCardProgram/KYC/useKYCStatus'
import { useCurrentOrgRootPath, useOrg } from './organization/useOrg'
import { HealthcheckType } from './types'
import { useGetNavigationPath } from './utils/healthcheckPaths'

type NavLinkProps = {
  to: string
  isNew?: boolean
  children: React.ReactNode
}
const NavLink = ({ to, isNew, children }: NavLinkProps) => {
  const orgRootPath = useCurrentOrgRootPath()
  const location = useLocation()
  const target = `${orgRootPath}${to.replace(orgRootPath, '')}`
  const isOnTarget = location.pathname === target
  if (isOnTarget) {
    return (
      <ListItem sx={(theme) => ({ p: theme.spacing(0.85, 1), alignItems: 'center', columnGap: 0.5 })}>
        <ListItemText
          disableTypography
          primary={
            <Grid container flexWrap="nowrap" justifyContent="space-between" alignItems="center">
              <Typography color="text.disabled" fontWeight="bold">
                {children}
              </Typography>
            </Grid>
          }
        />
        <ChevronRightIcon color="disabled" />
      </ListItem>
    )
  }
  return (
    <ListItem disablePadding disableGutters>
      <ListItemLink to={target} sx={{ columnGap: 0 }}>
        <ListItemText
          disableTypography
          primary={
            <Grid container flexWrap="nowrap" justifyContent="space-between" alignItems="center">
              <Typography color="primary" fontWeight="bold">
                {children}
              </Typography>

              {isNew && <Chip label="New" color="primary" size="small" sx={{ marginLeft: 1 }} />}
            </Grid>
          }
        />
      </ListItemLink>
    </ListItem>
  )
}
type NavigationProps = {
  drawerWidth: number
  hide?: boolean
}

type L = (props: { root?: string }) => React.ReactElement | null

const OrgDashboard = () => <NavLink to="">Org dashboard</NavLink>

const CompanyProfile = () => <NavLink to="/company-profile">Company roles</NavLink>
const _KYC = ({ org }: { org: OrganizationDetailFragment }) => {
  const kycStatus = useKYCStatus(org, org.onboardingWorkflow)
  const getNavigationPath = useGetNavigationPath(org)
  const to = getNavigationPath(HealthcheckType.CompleteKyc, kycStatus)
  if (!to) return null
  return <NavLink to={to}>Company Representative</NavLink>
}
const KYC = () => {
  const org = useOrg()
  if (!org) return null
  return <_KYC org={org} />
}
const PayrollOps = () => <NavLink to="/payroll">Payroll connection</NavLink>

const OrgBilling = () => <NavLink to="/billing">Billing</NavLink>
const AliceCardProgram = () => {
  const mayManageAliceCardProgram = useMayManageAliceCardProgram()
  if (!mayManageAliceCardProgram) return null
  return <NavLink to="/alice-card-program">Contributions Account</NavLink>
}
const EnrollmentSuccess = () => <NavLink to="/enrollment-success">Enrollment success</NavLink>
const SchedulePreshift = () => <NavLink to="/schedule-preshift">Schedule pre-shift</NavLink>
const EmployerDashBoard: L = ({ root = '' }) => <NavLink to={root}>Paygroup home</NavLink>
const People: L = ({ root = '' }) => {
  const org = useOrg()
  const isOnboarded = Boolean(org?.isOnboarded)
  const isPayrollConnected = isOnboarded || Boolean(org?.payrollConnection.payrollConnectedAt)

  if (!isPayrollConnected) return null
  return <NavLink to={`${root}/people`}>People</NavLink>
}
const PayPeriods: L = ({ root = '' }) => {
  const org = useOrg()
  if (!org?.isOnboarded) return null // won't have pay periods until we start processing benefits
  return <NavLink to={`${root}/pay-periods`}>Pay periods</NavLink>
}
const Documents: L = ({ root = '' }) => <NavLink to={`${root}/documents`}>Documents</NavLink>
const Locations: L = () => <NavLink to="/locations">Locations &amp; pay groups</NavLink>
const ShiftScheduling: L = () => <NavLink to="/shift-scheduling">Shift scheduling</NavLink>
const Profile: L = () => <NavLink to={`/profile`}>Email preferences</NavLink>

/**
 * Payroll reports are only relevant for particular payroll platforms that
 * are not sufficiently automated. Only display nav when the platform permits
 */
const PayrollReports: L = () => {
  const org = useOrg()
  if (!org) return null
  if (!org.isOnboarded) return null
  if (!org.payrollConnection.payrollPlatform?.permitsAdminReports) return null

  return <NavLink to="/payroll-reports">Payroll reports</NavLink>
}

const Reports: L = () => {
  const org = useOrg()
  if (!org) return null
  if (!org.isOnboarded) return null
  return <NavLink to="/reports">Reports</NavLink>
}

const EmployerNavigationList = ({ routingRoot }: { routingRoot: string }) => {
  const { data: employeeData } = useEmployeeQuery()
  const employee = employeeData?.employee
  if (!employee) return null

  return (
    <>
      <List disablePadding dense>
        <EmployerDashBoard root={routingRoot} />
        <PayPeriods root={routingRoot} />
        <People root={routingRoot} />
        <Documents root={routingRoot} />
      </List>
      <Divider sx={{ my: 1 }} />
      <List disablePadding dense>
        <OrgDashboard />
        <Profile />
      </List>
    </>
  )
}

const OrgNavigationList = () => (
  <List disablePadding dense>
    <Locations />
    <CompanyProfile />
    <KYC />
    <PayrollOps />
    <ShiftScheduling />
    <AliceCardProgram />
    <OrgBilling />
    <PayPeriods />
    <PayrollReports />
    <Reports />
    <EnrollmentSuccess />
    <SchedulePreshift />
    <People />
    <Documents />
    <Profile />
  </List>
)

export const OrgDashboardNavigation = ({ drawerWidth, hide }: NavigationProps) => {
  const params = useParams()
  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Toolbar />
      {hide ? null : params.employerId ? (
        <EmployerNavigationList routingRoot={`/${params.employerId}`} />
      ) : (
        <OrgNavigationList />
      )}
    </Drawer>
  )
}
